import {useState} from 'react';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import {useGetCampaignById} from '@tinct/core-api';
import {CampaignStatus} from '@tinct/core-api-models';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  MetricCard,
  TabNavLinks,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {LayoutTopbar, useBreadcrumb} from '@tinct/web-layout';
import {CircleAlert, CloudDownload, ExternalLink, Link2, Sparkles} from 'lucide-react';
import {toast} from 'sonner';
import {useStaticAssetsUrl} from "@tinct/core-utils";
import {usePreviewAndRefine} from './hooks/use-preview-and-refine';
import {useAutoRegenerateOutdatedPages} from '../../hooks/use-auto-regenerate-outdated-pages';
import {CampaignSetupProgress} from './components/campaign-setup-progress';
import {SnippetConnectBanner} from './components/snippet-connect-banner';
import {CampaignActionsMenu, getCampaignActionCapabilities} from './components/campaign-actions-menu';
import {ExportUrlsModal} from './components/export-urls-modal';
import {RenameCampaignModal} from '../../modals/rename-campaign-modal/rename-campaign-modal';
import {DeleteCampaignModal} from '../../modals/delete-campaign-modal/delete-campaign-modal';
import {UnpublishCampaignModal} from '../../modals/unpublish-campaign-modal/unpublish-campaign-modal';
import {UnpublishLandingPageModal} from '../../modals/unpublish-landing-page-modal/unpublish-landing-page-modal';
import {AudienceConflictModal} from '../../funnels/steps/step4-audience/audience-conflict-modal';
import {CampaignAxisBadge, PageOutdatedStatusBadge} from '../shared';


const STATUS_CONFIG: Record<
  CampaignStatus,
  { label: string; dotColor: string }
> = {
  published: { label: 'Published', dotColor: 'bg-teal-500' },
  draft: { label: 'Draft', dotColor: 'bg-amber-500' },
  unpublished: { label: 'Unpublished', dotColor: 'bg-red-500' },
  ready: { label: 'Ready', dotColor: 'bg-blue-500' },
};

function CampaignStatusBadge({ status }: { status: CampaignStatus | null }) {
  if (!status) return null;
  const config = STATUS_CONFIG[status];
  return (
    <Badge variant="secondary" className="gap-1.5">
      <span className={`size-2 rounded-full ${config.dotColor}`} />
      {config.label}
    </Badge>
  );
}

export function DetailsCampaignPage() {
  const { id = '' } = useParams<{ id: string }>();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const navigate = useNavigate();
  const assetsUrl = useStaticAssetsUrl();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // Snapshot taken once per mount: reading the clock during render is impure
  const [nowMs] = useState(() => Date.now());
  const {
    conflicts,
    showConflictModal,
    setShowConflictModal,
    isValidating,
    handlePreviewAndRefine,
    handleExcludeAndContinue,
  } = usePreviewAndRefine(id);

  const breadcrumbs = useBreadcrumb([
    { label: 'Dashboard', to: `${workspaceBaseUrl}/dashboard` },
    { label: 'Campaigns', to: `${workspaceBaseUrl}/campaigns` },
  ], true);

  const { data: campaign, isLoading } = useGetCampaignById({
    id,
    fields: {
      id: null,
      name: null,
      status: null,
      outdatedStatus: null,
      pagesToRegenerateCount: null,
      landingPageOriginType: null,
      landingPageVariationAxis: null,
      landingPage: {
        fields: {
          id: null,
          url: null,
          status: null,
          publishedUrl: null,
          previewUrl: null,
        },
      },
      audience: {
        fields: {
          name: null,
        },
      },
      statistics: {
        fields: {
          leadsCount: null,
          totalPagesCount: null,
          publishedPagesCount: null,
          excludedPagesCount: null,
          // Gates the Delete action (see getCampaignActionCapabilities).
          eventsCount: null,
        },
      },
      firstPublishedAt: null,
      lastUnpublishedJob: {
        fields: {
          createdAt: null,
        },
      },
      createdAt: null,
      updatedAt: null,
      confirmedSteps: null,
      lastAnalysisJob: null,
      lastPublishedJob: null,
      snippetStatus: null,
      samplePages: {
        limit: 5,
        offset: 0,
        fields: {
          status: null,
        },
      },
    },
  });

  // Pages an edit outdated get regenerated as soon as the campaign is seen (or the paywall opens).
  useAutoRegenerateOutdatedPages(campaign);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="text-muted-foreground">Loading campaign...</span>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="text-muted-foreground">Campaign not found</span>
      </div>
    );
  }

  const campaignName = campaign.name ?? 'Untitled Campaign';
  const publishedPagesCount = campaign.statistics?.publishedPagesCount ?? 0;
  const excludedPagesCount = campaign.statistics?.excludedPagesCount ?? 0;
  const leadsCount = campaign.statistics?.leadsCount ?? 0;
  const companiesWithoutPage = leadsCount - publishedPagesCount - excludedPagesCount;

  const formatDuration = (fromMs: number, toMs: number) => {
    const pluralize = (value: number, unit: string) =>
      `${value} ${unit}${value === 1 ? '' : 's'}`;
    const diffSec = Math.floor((toMs - fromMs) / 1000);
    if (diffSec < 60) return pluralize(diffSec, 'second');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return pluralize(diffMin, 'minute');
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return pluralize(diffHours, 'hour');
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return pluralize(diffDays, 'day');
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return pluralize(diffMonths, 'month');
    const diffYears = Math.floor(diffMonths / 12);
    return pluralize(diffYears, 'year');
  };

  const isUnpublished = campaign.status === 'unpublished';
  const unpublishedAt = campaign.lastUnpublishedJob?.createdAt;
  const publishedAtMs = campaign.firstPublishedAt ? new Date(campaign.firstPublishedAt).getTime() : null;

  const runningFor = publishedAtMs
    ? formatDuration(publishedAtMs, isUnpublished && unpublishedAt ? new Date(unpublishedAt).getTime() : nowMs)
    : '—';

  const isSetupIncomplete = campaign.status === 'draft';
  const tabItems = [
    {
      title: 'Analytics',
      url: `${workspaceBaseUrl}/campaigns/${id}`,
    },
    {
      title: 'Page variants',
      url: `${workspaceBaseUrl}/campaigns/${id}/landing-pages`,
      disabled: isSetupIncomplete,
    },
    // Lead-form submissions only exist on hosted (Tinct-generated) pages.
    ...(campaign.landingPageOriginType === 'new'
      ? [
          {
            title: 'Leads',
            url: `${workspaceBaseUrl}/campaigns/${id}/leads`,
            disabled: isSetupIncomplete,
          },
        ]
      : []),
    {
      title: 'Settings',
      url: `${workspaceBaseUrl}/campaigns/${id}/settings`,
      disabled: isSetupIncomplete,
    },
  ];

  const exportBlockedReason = getCampaignActionCapabilities(campaign).export.reason;

  const isNewOrigin = campaign.landingPageOriginType === 'new';
  // NEW pages live on their Tinct address (previewUrl until first publish);
  // EXISTING campaigns keep pointing at the customer's own URL.
  const hostedPageUrl =
    campaign.landingPage?.publishedUrl ?? campaign.landingPage?.previewUrl ?? null;
  const campaignUrl = isNewOrigin ? hostedPageUrl : campaign.landingPage?.url ?? null;

  const handleCopyCampaignUrl = () => {
    if (!campaignUrl) return;
    navigator.clipboard.writeText(campaignUrl);
    toast.success('URL copied to clipboard');
  };

  const totalPagesCount = campaign.statistics?.totalPagesCount ?? 0;
  const showCreateVariantsBanner =
    isNewOrigin && campaign.status === 'published' && totalPagesCount === 0;

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar
          breadcrumbs={breadcrumbs}
        />
      </header>
      {/* Top section */}
      <div className="px-8 pt-8">
        <div className="flex flex-col gap-12 max-w-6xl mx-auto">
          <div className="flex flex-col gap-6">
         {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{campaignName}</h1>
              <div className="flex min-w-0 items-center gap-2 pt-1">
                <CampaignStatusBadge status={campaign.status ?? null} />
                <PageOutdatedStatusBadge status={campaign.outdatedStatus} />
                <CampaignAxisBadge axis={campaign.landingPageVariationAxis} />
                {isNewOrigin && (
                  <Badge variant="secondary" className="gap-1.5">
                    <Sparkles className="size-3" />
                    Generated by Tinct.ai
                  </Badge>
                )}
                {campaign.landingPage?.url && (
                  <span
                    className="max-w-[320px] truncate text-sm text-muted-foreground"
                    title={campaign.landingPage.url}
                  >
                    {campaign.landingPage.url}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {isNewOrigin && hostedPageUrl && (
                <Button variant="outline" asChild>
                  <a href={hostedPageUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-4" />
                    Preview
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                disabled={!campaignUrl}
                onClick={handleCopyCampaignUrl}
              >
                <Link2 className="size-4" />
                Copy URL
              </Button>
              {exportBlockedReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-not-allowed">
                      <Button disabled>
                        <CloudDownload className="size-4" />
                        Export URLs
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{exportBlockedReason}</TooltipContent>
                </Tooltip>
              ) : (
                <Button onClick={() => setIsExportModalOpen(true)}>
                  <CloudDownload className="size-4" />
                  Export URLs
                </Button>
              )}
              <CampaignActionsMenu
                campaign={campaign}
                onRename={() => setIsRenameModalOpen(true)}
                onPublish={() =>
                  // NEW origin: the page's Publish button lives on the edit
                  // step. EXISTING: go straight to the publish step — the
                  // review surface is the studio, reachable on its own.
                  navigate(
                    `${workspaceBaseUrl}/funnel-campaigns/${id}/${isNewOrigin ? 'edit' : 'publish'}`
                  )
                }
                onUnpublish={() => setIsUnpublishModalOpen(true)}
                onDelete={() => setIsDeleteModalOpen(true)}
              />
            </div>
          </div>

          {campaign.status === 'draft' || campaign.status === 'ready' ? (
            <CampaignSetupProgress campaign={campaign} />
          ) : (
            <>
              {/* Alert Banner */}
              {companiesWithoutPage > 0 && (
                <Alert className="flex items-center gap-3 rounded-2xl border-indigo-100 bg-indigo-50 p-4 text-indigo-600">
                  <CircleAlert className="size-4 shrink-0" />
                  <div className="flex flex-1 flex-col gap-2">
                    <AlertTitle className="text-sm font-medium">{companiesWithoutPage} {companiesWithoutPage === 1 ? 'company' : 'companies'} without a live page variant</AlertTitle>
                    <AlertDescription className="text-sm text-indigo-600">
                      This campaign has an audience of {leadsCount} {leadsCount === 1 ? 'company' : 'companies'}. Only {publishedPagesCount} {publishedPagesCount === 1 ? 'page is' : 'pages are'} live right now, and {excludedPagesCount} {excludedPagesCount === 1 ? 'is' : 'are'} excluded.
                    </AlertDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 rounded-md bg-white text-xs text-foreground"
                    onClick={handlePreviewAndRefine}
                    disabled={isValidating}
                  >
                    Manage
                  </Button>
                </Alert>
              )}

              {/* Stat Cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <MetricCard
                  headerText={isUnpublished ? 'Was live for' : 'Running for'}
                  statisticText={runningFor}
                  descriptionText={
                    isUnpublished && unpublishedAt
                      ? `Unpublished ${new Date(unpublishedAt).toLocaleDateString('en-GB')}`
                      : campaign.firstPublishedAt
                        ? `First publication ${new Date(campaign.firstPublishedAt).toLocaleDateString('en-GB')}`
                        : '—'
                  }
                  icon={<img src={`${assetsUrl}/chrono.png`} alt={"Chrono"}/>}
                />
                <MetricCard
                  headerText="Page variants"
                  statisticText={`${publishedPagesCount}`}
                  descriptionText={`/${leadsCount}`}
                  icon={<img src={`${assetsUrl}/windows.png`} alt={"Windows"}/>}
                />
                <MetricCard
                  headerText="Targets"
                  statisticText={`${leadsCount} ${leadsCount === 1 ? 'company' : 'companies'}`}
                  descriptionText={`Audience : ${campaign.audience?.name ?? '—'}`}
                  icon={<img src={`${assetsUrl}/target.png`} alt={"Windows"}/>}
                />
              </div>

              {/* NEW pages ship the snippet inside the generated HTML — no install step. */}
              {showCreateVariantsBanner && (
                <Alert className="flex items-center gap-3 rounded-2xl border-indigo-100 bg-indigo-50 p-4 text-indigo-600">
                  <CircleAlert className="size-4 shrink-0" />
                  <div className="flex flex-1 flex-col gap-2">
                    <AlertTitle className="text-sm font-medium">No variants yet</AlertTitle>
                    <AlertDescription className="text-sm text-indigo-600">
                      Adapt this page for different target accounts without rebriefing — same
                      brand, tailored messaging for each company.
                    </AlertDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 rounded-md bg-white text-xs text-foreground"
                    onClick={() => navigate(`${workspaceBaseUrl}/funnel-campaigns/${id}/audience`)}
                  >
                    Create page variants
                  </Button>
                </Alert>
              )}
              {!isNewOrigin &&
                campaign.status === 'published' &&
                campaign.snippetStatus?.status !== 'active' && <SnippetConnectBanner />}
            </>
          )}
          </div>

          {/* Tabs */}
          <TabNavLinks items={tabItems} className="px-0" />
        </div>
      </div>

      {/* Tab content section */}
      <div className="px-8 py-12">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          <Outlet />
          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border pt-4 text-sm text-muted-foreground">
            <span>
              Created{' '}
              {campaign.createdAt
                ? new Date(campaign.createdAt).toLocaleDateString('en-GB')
                : '—'}
            </span>
            <span>
              Last updated{' '}
              {campaign.updatedAt
                ? new Date(campaign.updatedAt).toLocaleDateString('en-GB')
                : '—'}
            </span>
          </div>
        </div>
      </div>

      <AudienceConflictModal
        open={showConflictModal}
        onOpenChange={setShowConflictModal}
        conflicts={conflicts}
        onExcludeAndContinue={handleExcludeAndContinue}
        isPending={isValidating}
      />

      <ExportUrlsModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        campaignId={id}
        campaignName={campaignName}
      />

      <RenameCampaignModal
        campaign={campaign}
        open={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
      />

      {isNewOrigin ? (
        <UnpublishLandingPageModal
          campaign={campaign}
          open={isUnpublishModalOpen}
          onOpenChange={setIsUnpublishModalOpen}
        />
      ) : (
        <UnpublishCampaignModal
          row={campaign}
          open={isUnpublishModalOpen}
          onOpenChange={setIsUnpublishModalOpen}
        />
      )}

      <DeleteCampaignModal
        row={campaign}
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onDeleted={() => navigate(`${workspaceBaseUrl}/campaigns`)}
      />
    </div>
  );
}
